import Api from '@/api'
import { chunk, PROJECT_FILE_MAX_FILES_PER_REQUEST } from '@/utils/projectFiles'

/**
 * Documents that belong to a project itself (SWMS, inductions, plans, permits…).
 * Deliberately its own module: `project.files` is the tradie-file list used by
 * the tradie view, and mixing the two is exactly the confusion this feature
 * exists to remove.
 */

// Only the latest list request may write state, so a slow response for an old
// search/page can't overwrite a newer one.
let listRequestSeq = 0

const emptyMeta = () => ({
  current_page: 1,
  last_page: 1,
  per_page: 20,
  total: 0,
})

// null = not loaded yet (the tab badges stay blank rather than showing a wrong 0).
// `expired` = active files past their expiry date (the marker on the Files tab).
const emptyCounts = () => ({ active: null, archived: null, expired: null })

const errorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data
  return (data && data.message) || fallback
}

export default {
  namespaced: true,

  state: {
    files: [],
    meta: emptyMeta(),
    counts: emptyCounts(),
    // Tags in use in the current view, most used first (drives the filter chips).
    tagOptions: [],
    // True when the project is only shared with us: the API tells us so, and the
    // panel hides every write control and the Archived tab.
    readOnly: false,
    loading: false,
    loadError: '',
    uploading: false,
  },

  getters: {
    files: (state) => state.files,
    meta: (state) => state.meta,
    counts: (state) => state.counts,
    tagOptions: (state) => state.tagOptions,
    readOnly: (state) => state.readOnly,
    loading: (state) => state.loading,
    loadError: (state) => state.loadError,
    uploading: (state) => state.uploading,
  },

  mutations: {
    setFiles(state, files) {
      state.files = files
    },
    setMeta(state, meta) {
      state.meta = { ...emptyMeta(), ...(meta || {}) }
    },
    setCounts(state, counts) {
      state.counts = { ...emptyCounts(), ...(counts || {}) }
    },
    setTagOptions(state, tags) {
      state.tagOptions = Array.isArray(tags) ? tags : []
    },
    setReadOnly(state, value) {
      state.readOnly = !!value
    },
    // Apply +/- deltas to whichever counts are known, never below zero.
    adjustCounts(state, delta) {
      const next = { ...state.counts }
      ;['active', 'archived', 'expired'].forEach((key) => {
        if (delta[key] && typeof next[key] === 'number') {
          next[key] = Math.max(0, next[key] + delta[key])
        }
      })
      state.counts = next
    },
    setLoading(state, value) {
      state.loading = !!value
    },
    setLoadError(state, message) {
      state.loadError = message || ''
    },
    setUploading(state, value) {
      state.uploading = !!value
    },
    patchFile(state, file) {
      const idx = state.files.findIndex((f) => f.id === file.id)
      if (idx !== -1)
        state.files.splice(idx, 1, { ...state.files[idx], ...file })
    },
    removeFile(state, id) {
      state.files = state.files.filter((f) => f.id !== id)
      state.meta = {
        ...state.meta,
        total: Math.max(0, (state.meta.total || 0) - 1),
      }
    },
    reset(state) {
      listRequestSeq++
      state.files = []
      state.meta = emptyMeta()
      state.counts = emptyCounts()
      state.tagOptions = []
      state.readOnly = false
      state.loading = false
      state.loadError = ''
      state.uploading = false
    },
  },

  actions: {
    async fetchFiles(
      { commit },
      {
        projectId,
        page = 1,
        search = '',
        perPage = 20,
        archived = false,
        tag = '',
      },
    ) {
      const seq = ++listRequestSeq
      commit('setLoading', true)
      commit('setLoadError', '')

      try {
        const params = { page, per_page: perPage }
        if (search) params.search = search
        if (tag) params.tag = tag
        if (archived) params.archived = 1

        const { data } = await Api.get(`projects/${projectId}/files`, {
          params,
        })
        if (seq !== listRequestSeq) return

        commit('setFiles', data.data || [])
        commit('setMeta', data.meta)
        if (data.counts) commit('setCounts', data.counts)
        commit('setTagOptions', data.tag_options)
        commit('setReadOnly', !!(data.access && data.access.read_only))
      } catch (error) {
        if (seq !== listRequestSeq) return
        commit('setFiles', [])
        commit('setMeta', null)
        commit('setTagOptions', [])
        commit(
          'setLoadError',
          errorMessage(
            error,
            'We couldn’t load the project files. Please try again.',
          ),
        )
      } finally {
        if (seq === listRequestSeq) commit('setLoading', false)
      }
    },

    /**
     * Upload one or many files. They go up in batches (the API caps a request),
     * and a failed batch never stops the ones after it. `expiresAt` (YYYY-MM-DD)
     * and `tags` apply to every file in the call.
     *
     * @returns {{ uploaded: number, failed: Array<{ name: string, message: string }> }}
     */
    async uploadFiles(
      { commit },
      { projectId, files, expiresAt = null, tags = [] },
    ) {
      commit('setUploading', true)

      let uploaded = 0
      const failed = []

      try {
        const batches = chunk(
          Array.from(files),
          PROJECT_FILE_MAX_FILES_PER_REQUEST,
        )

        for (const batch of batches) {
          const formData = new FormData()
          batch.forEach((file) => formData.append('files[]', file))
          if (expiresAt) formData.append('expires_at', expiresAt)
          tags.forEach((tag) => formData.append('tags[]', tag))

          try {
            const { data } = await Api.post(
              `projects/${projectId}/files`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
              },
            )
            uploaded += (data.data || []).length
            ;(data.failed || []).forEach((f) =>
              failed.push({ name: f.name, message: f.message }),
            )
          } catch (error) {
            const data = error.response && error.response.data
            const message = errorMessage(
              error,
              'The upload failed. Please try again.',
            )

            if (data && Array.isArray(data.failed) && data.failed.length) {
              data.failed.forEach((f) =>
                failed.push({ name: f.name, message: f.message }),
              )
            } else {
              batch.forEach((file) => failed.push({ name: file.name, message }))
            }
          }
        }
      } finally {
        commit('setUploading', false)
      }

      return { uploaded, failed }
    },

    /**
     * Change a file's name, expiry date and/or tags. Only the fields you pass
     * are sent, so editing one never overwrites another. Pass `expiresAt: null`
     * to clear the expiry and `tags: []` to clear the tags.
     */
    async updateFile({ commit }, { projectId, id, name, expiresAt, tags }) {
      const body = {}
      if (name !== undefined) body.name = name
      if (expiresAt !== undefined) body.expires_at = expiresAt
      if (tags !== undefined) body.tags = tags

      const { data } = await Api.put(`projects/${projectId}/files/${id}`, body)
      commit('patchFile', data.data)
      return data.data
    },

    // Swap the stored document for another (any allowed type). Its name, tags
    // and expiry are untouched; a separate POST because a file can't ride on
    // the PUT that edits those.
    async replaceFile({ commit }, { projectId, id, file }) {
      const formData = new FormData()
      formData.append('file', file)

      const { data } = await Api.post(
        `projects/${projectId}/files/${id}/replace`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      commit('patchFile', data.data)
      return data.data
    },

    // `archived` says which view the file was in, so the right badge drops.
    // `expired` says the file was past its expiry date: only an ACTIVE expired
    // file counts towards the tab's "N expired", so deleting one from the
    // archive leaves that number alone.
    async deleteFile(
      { commit },
      { projectId, id, archived = false, expired = false },
    ) {
      await Api.delete(`projects/${projectId}/files/${id}`)
      commit('removeFile', id)
      commit(
        'adjustCounts',
        archived ? { archived: -1 } : { active: -1, expired: expired ? -1 : 0 },
      )
    },

    /** Hide a file from the default list (reversible). Leaves the current view. */
    async archiveFile({ commit }, { projectId, id, expired = false }) {
      await Api.post(`projects/${projectId}/files/${id}/archive`)
      commit('removeFile', id)
      commit('adjustCounts', {
        active: -1,
        archived: 1,
        expired: expired ? -1 : 0,
      })
    },

    /** Bring an archived file back to the default list. Leaves the current view. */
    async unarchiveFile({ commit }, { projectId, id, expired = false }) {
      await Api.post(`projects/${projectId}/files/${id}/unarchive`)
      commit('removeFile', id)
      commit('adjustCounts', {
        active: 1,
        archived: -1,
        expired: expired ? 1 : 0,
      })
    },

    /**
     * Signed URL to show the file inline: `{ kind: 'image' | 'pdf' | null, url }`.
     * `kind: null` means the browser can't show this type (Word, CSV, Excel).
     */
    async getPreview(_, { projectId, id }) {
      const { data } = await Api.get(
        `projects/${projectId}/files/${id}/preview`,
      )
      return data.data
    },

    /** Signed, readable-name download URL. */
    async getDownloadUrl(_, { projectId, id }) {
      const { data } = await Api.get(
        `projects/${projectId}/files/${id}/download`,
      )
      return data.data
    },
  },
}
